/**
 * Created: 2026-04-08
 * Initial commit: Split SlotsPage into SlotsPage/ directory (feat/event/mr-10)
 *
 * Hook encapsulating derived action state and slot mutation handlers.
 */

import { gql } from '@apollo/client'
import { useMutation } from '@apollo/client/react'
import { useMemo, useState } from 'react'
import { OverrideSlotsDocument, SlotStatus } from '../../gql/graphql'
import type { SlotMeta } from './types'

// Created: 2026-04-08 | Initial commit: Split SlotsPage into SlotsPage/ directory (feat/event/mr-10)
gql`
  mutation OverrideSlots($slots: [SlotOverrideInput!]!) {
    overrideSlots(slots: $slots) {
      ... on GraphqlSuccess {
        success
      }
      ... on GraphqlRequestError {
        code
        message
      }
    }
  }
`

interface Court {
  id: string
  name: string
  displayOrder: number
  sport: string
}

interface UseSlotActionsParams {
  selectedCells: { hour: number; courtId: string }[]
  setSelectedCells: React.Dispatch<
    React.SetStateAction<{ hour: number; courtId: string }[]>
  >
  slotLookup: Map<string, SlotMeta>
  courts: Court[]
  slotsRefetch: () => void
}

export function useSlotActions({
  selectedCells,
  setSelectedCells,
  slotLookup,
  courts,
  slotsRefetch,
}: UseSlotActionsParams) {
  const [overrideSlots, { loading: mutating }] = useMutation(OverrideSlotsDocument)

  const [reasonDialogOpen, setReasonDialogOpen] = useState(false)
  const [reasonInput, setReasonInput] = useState('')
  const [priceDialogOpen, setPriceDialogOpen] = useState(false)
  const [priceInput, setPriceInput] = useState('')

  const { hasOpen, hasReserved, canCreateEvent, eventSlots } = useMemo(() => {
    let open = false
    let reserved = false
    for (const cell of selectedCells) {
      const meta = slotLookup.get(`${cell.hour}-${cell.courtId}`)
      if (!meta) continue
      if (meta.status === SlotStatus.Open) open = true
      if (meta.status === SlotStatus.Reserved && !meta.eventId) reserved = true
    }

    const allOpen =
      selectedCells.length > 0 &&
      selectedCells.every(
        (c) => slotLookup.get(`${c.hour}-${c.courtId}`)?.status === SlotStatus.Open,
      )

    const courtHoursMap = new Map<string, number[]>()
    for (const cell of selectedCells) {
      if (!courtHoursMap.has(cell.courtId)) courtHoursMap.set(cell.courtId, [])
      courtHoursMap.get(cell.courtId)?.push(cell.hour)
    }
    const courtIdList = [...courtHoursMap.keys()]

    const firstHours =
      courtIdList.length > 0
        ? [...(courtHoursMap.get(courtIdList[0]) ?? [])].sort((a, b) => a - b)
        : []
    const sameHours =
      courtIdList.length > 0 &&
      courtIdList.every((id) => {
        const sorted = [...(courtHoursMap.get(id) ?? [])].sort((a, b) => a - b)
        return (
          sorted.length === firstHours.length &&
          sorted.every((h, i) => h === firstHours[i])
        )
      })
    const consecutive =
      firstHours.length > 0 &&
      firstHours.every((h, i) => i === 0 || h === firstHours[i - 1] + 1)

    const courtCategoryMap = new Map(courts.map((c) => [c.id, c.sport]))
    const categories = new Set(courtIdList.map((id) => courtCategoryMap.get(id)))
    const sameCategory = categories.size === 1

    const courtNameMap = new Map(courts.map((c) => [c.id, c.name]))
    const canCreate = allOpen && sameHours && consecutive && sameCategory
    const slots = canCreate
      ? selectedCells.map((c) => ({
          courtId: c.courtId,
          courtName: courtNameMap.get(c.courtId) ?? '',
          startTime: slotLookup.get(`${c.hour}-${c.courtId}`)?.startTime ?? '',
        }))
      : []

    return {
      hasOpen: open,
      hasReserved: reserved,
      canCreateEvent: canCreate,
      eventSlots: slots,
    }
  }, [selectedCells, slotLookup, courts])

  const handleReserveOpen = () => {
    setReasonInput('')
    setReasonDialogOpen(true)
  }

  const handleReserveConfirm = async () => {
    const inputs = []
    for (const cell of selectedCells) {
      const meta = slotLookup.get(`${cell.hour}-${cell.courtId}`)
      if (meta?.status !== SlotStatus.Open) continue
      inputs.push({
        courtId: cell.courtId,
        startTime: meta.startTime,
        status: SlotStatus.Reserved,
        reason: reasonInput || undefined,
      })
    }
    if (inputs.length === 0) return
    setReasonDialogOpen(false)
    await overrideSlots({ variables: { slots: inputs } })
    setSelectedCells([])
    await slotsRefetch()
  }

  const handleRelease = async () => {
    const inputs = []
    for (const cell of selectedCells) {
      const meta = slotLookup.get(`${cell.hour}-${cell.courtId}`)
      if (meta?.status !== SlotStatus.Reserved || meta.eventId) continue
      inputs.push({
        courtId: cell.courtId,
        startTime: meta.startTime,
        status: SlotStatus.Open,
      })
    }
    if (inputs.length === 0) return
    await overrideSlots({ variables: { slots: inputs } })
    setSelectedCells([])
    slotsRefetch()
  }

  const handleChangePriceOpen = () => {
    setPriceInput('')
    setPriceDialogOpen(true)
  }

  const handleChangePriceConfirm = async () => {
    if (priceInput === '' || Number(priceInput) < 0 || Number(priceInput) > 999) return
    const price = priceInput
    const inputs = []
    for (const cell of selectedCells) {
      const meta = slotLookup.get(`${cell.hour}-${cell.courtId}`)
      if (
        !meta ||
        (meta.status !== SlotStatus.Open && meta.status !== SlotStatus.Reserved) ||
        (meta.status === SlotStatus.Reserved && meta.eventId)
      )
        continue
      inputs.push({ courtId: cell.courtId, startTime: meta.startTime, price })
    }
    if (inputs.length === 0) return
    setPriceDialogOpen(false)
    await overrideSlots({ variables: { slots: inputs } })
    setSelectedCells([])
    slotsRefetch()
  }

  return {
    hasOpen,
    hasReserved,
    canCreateEvent,
    eventSlots,
    mutating,
    reasonDialogOpen,
    setReasonDialogOpen,
    reasonInput,
    setReasonInput,
    priceDialogOpen,
    setPriceDialogOpen,
    priceInput,
    setPriceInput,
    handleReserveOpen,
    handleReserveConfirm,
    handleRelease,
    handleChangePriceOpen,
    handleChangePriceConfirm,
  }
}
